import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { TBS_API_URL, TBS_TENANT_ID } from "../../config";
import type {
  DianPendingResponse,
  DianRecordInput,
  Invoice,
  InvoiceInput,
  InvoiceList,
  InvoiceListParams,
} from "../../types/tbs/invoices";

// Tiendas Biai — invoices. The POS is a client of `createInvoice` and little else.

const qs = (params: Record<string, string | number | boolean | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === false) continue;
    search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
};

export const tbsInvoicesApi = createApi({
  reducerPath: "tbsInvoicesApi",
  tagTypes: ["TbsInvoice", "TbsItemStock"],
  baseQuery: fetchBaseQuery({
    baseUrl: TBS_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      if (TBS_TENANT_ID) headers.set("X-Tenant-Id", TBS_TENANT_ID);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getInvoices: builder.query<InvoiceList, InvoiceListParams | void>({
      query: (params) => `/v1/invoices${qs({ ...(params ?? {}) })}`,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((i) => ({ type: "TbsInvoice" as const, id: i.id })),
              { type: "TbsInvoice" as const, id: "LIST" },
            ]
          : [{ type: "TbsInvoice" as const, id: "LIST" }],
    }),

    getInvoice: builder.query<Invoice, string>({
      query: (id) => `/v1/invoices/${id}`,
      providesTags: (_r, _e, id) => [{ type: "TbsInvoice", id }],
    }),

    /**
     * **The sale.**
     *
     * `Idempotency-Key` is mandatory and the caller supplies it, because it has to be *the same key*
     * on a retry — a key generated here would be new on every attempt, which is precisely the
     * duplicate this mechanism exists to prevent. The POS mints one when the cashier starts a sale
     * and keeps it until that sale succeeds.
     *
     * Invalidates stock as well as invoices: a sale moves both, and a POS grid showing yesterday's
     * quantity is how the next cashier oversells.
     */
    createInvoice: builder.mutation<Invoice, { body: InvoiceInput; idempotencyKey: string }>({
      query: ({ body, idempotencyKey }) => ({
        url: "/v1/invoices",
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body,
      }),
      invalidatesTags: [
        { type: "TbsInvoice", id: "LIST" },
        { type: "TbsItemStock", id: "LIST" },
      ],
    }),

    /** The re-keying queue. A list that should normally be empty, and whose emptiness is the point. */
    getDianPending: builder.query<DianPendingResponse, void>({
      query: () => "/v1/invoices/dian-pending",
      providesTags: [{ type: "TbsInvoice", id: "DIAN_PENDING" }],
    }),

    /**
     * Recording what the portal gave back.
     *
     * **Idempotent on the CUFE, not on an `Idempotency-Key`** — the realistic failure is a cashier
     * pasting the same CUFE twice, and the server answers the stored result with
     * `Idempotency-Replayed: true`. A *different* CUFE on a recorded invoice is a 409, never an
     * overwrite.
     */
    recordDian: builder.mutation<Invoice, { id: string; body: DianRecordInput }>({
      query: ({ id, body }) => ({ url: `/v1/invoices/${id}/dian-record`, method: "POST", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "TbsInvoice", id },
        { type: "TbsInvoice", id: "LIST" },
        { type: "TbsInvoice", id: "DIAN_PENDING" },
      ],
    }),

    /** A presigned PUT for the signed XML. **The key is what gets stored**, never the URL (EB-515). */
    getDianXmlUrl: builder.query<{ url: string; key: string; expires_in: number }, string>({
      query: (id) => `/v1/invoices/${id}/dian-xml-url`,
      keepUnusedDataFor: 0,
    }),

    voidInvoice: builder.mutation<Invoice, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/v1/invoices/${id}/void`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "TbsInvoice", id },
        { type: "TbsInvoice", id: "LIST" },
        { type: "TbsItemStock", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useGetInvoiceQuery,
  useCreateInvoiceMutation,
  useVoidInvoiceMutation,
  useGetDianPendingQuery,
  useRecordDianMutation,
  useLazyGetDianXmlUrlQuery,
} = tbsInvoicesApi;
